import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import UserLink from '../../components/common/UserLink';
import { formatDateTime } from '../../utils/regionalFormat';

export function formatClock(ms) {
  if (ms == null) return '—';
  const clamped = Math.max(0, ms);
  if (clamped < 10000) {
    const tenthsTotal = Math.floor(clamped / 100);
    const s = Math.floor(tenthsTotal / 10);
    const d = tenthsTotal % 10;
    return `0:${String(s).padStart(2, '0')}.${d}`;
  }
  // Ceil whole seconds so a fresh 10:00 clock does not flash 9:59 after 1ms of lag.
  const total = Math.max(1, Math.ceil(clamped / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatTimeControl(seconds, incrementSec = 0, daysPerMove = null) {
  if (daysPerMove != null && Number(daysPerMove) > 0) {
    return i18n.t('playing.timeControl.daysPerMove', {
      count: Number(daysPerMove),
      defaultValue: '{{count}}d/move',
    });
  }
  if (seconds == null || Number(seconds) <= 0) return null;
  const m = Math.round(seconds / 60);
  return incrementSec ? `${m}+${incrementSec}` : `${m}+0`;
}

/** Live remaining time until ISO deadline: localized compact units. */
export function formatDeadlineCountdown(deadlineIso, nowMs = Date.now()) {
  if (!deadlineIso) return null;
  const end = Date.parse(deadlineIso);
  if (!Number.isFinite(end)) return null;
  let ms = end - nowMs;
  if (ms <= 0) {
    return i18n.t('playing.countdown.zeroSeconds', { defaultValue: '0s' });
  }
  const day = 86_400_000;
  const hour = 3_600_000;
  const min = 60_000;
  if (ms >= day) {
    const d = Math.floor(ms / day);
    const h = Math.floor((ms % day) / hour);
    return h > 0
      ? i18n.t('playing.countdown.daysHours', { d, h, defaultValue: '{{d}}d {{h}}h' })
      : i18n.t('playing.countdown.days', { d, defaultValue: '{{d}}d' });
  }
  if (ms >= hour) {
    const h = Math.floor(ms / hour);
    const m = Math.floor((ms % hour) / min);
    return m > 0
      ? i18n.t('playing.countdown.hoursMinutes', { h, m, defaultValue: '{{h}}h {{m}}m' })
      : i18n.t('playing.countdown.hours', { h, defaultValue: '{{h}}h' });
  }
  if (ms >= min) {
    return i18n.t('playing.countdown.minutes', {
      m: Math.floor(ms / min),
      defaultValue: '{{m}}m',
    });
  }
  return i18n.t('playing.countdown.seconds', {
    s: Math.max(1, Math.ceil(ms / 1000)),
    defaultValue: '{{s}}s',
  });
}

export function deadlineUrgency(deadlineIso, nowMs = Date.now()) {
  if (!deadlineIso) return 'ok';
  const end = Date.parse(deadlineIso);
  if (!Number.isFinite(end)) return 'ok';
  const left = end - nowMs;
  if (left <= 0) return 'critical';
  if (left < 3_600_000) return 'critical'; // <1h
  if (left < 86_400_000) return 'warn'; // <1d
  return 'ok';
}

/** Align with backend sweepers: seeks 10m, live pending 5m, corr challenges 48h. */
export const SEEK_TTL_MS = 10 * 60 * 1000;
export const LIVE_CHALLENGE_TTL_MS = 5 * 60 * 1000;
export const CORR_CHALLENGE_TTL_MS = 48 * 60 * 60 * 1000;

export function formatExpiryCountdown(createdAtIso, ttlMs, nowMs = Date.now()) {
  if (!createdAtIso || !ttlMs) return null;
  const start = Date.parse(createdAtIso);
  if (!Number.isFinite(start)) return null;
  if (nowMs >= start + ttlMs) return null;
  const endIso = new Date(start + ttlMs).toISOString();
  return formatDeadlineCountdown(endIso, nowMs);
}

/** True when createdAt + ttlMs is at or before now. */
export function isPastTtl(createdAtIso, ttlMs, nowMs = Date.now()) {
  if (!createdAtIso || !ttlMs) return false;
  const start = Date.parse(createdAtIso);
  if (!Number.isFinite(start)) return false;
  return nowMs >= start + ttlMs;
}

export function challengeTtlMs(challenge) {
  if (challenge?.daysPerMove != null && Number(challenge.daysPerMove) > 0) {
    return CORR_CHALLENGE_TTL_MS;
  }
  return LIVE_CHALLENGE_TTL_MS;
}

export function formatResultReason(reason, t) {
  if (!reason) return null;
  return t(`playing.resultReason.${reason}`, { defaultValue: reason });
}

export function formatReportReason(reason, t) {
  if (!reason) return null;
  const map = {
    SUSPECTED_ASSISTANCE: 'playing.reportReason.assistance',
    ABUSIVE_CHAT: 'playing.reportReason.chat',
    STALLING: 'playing.reportReason.stalling',
    OTHER: 'playing.reportReason.other',
  };
  const key = map[String(reason)] || `playing.reportReason.${reason}`;
  return t(key, { defaultValue: String(reason).replace(/_/g, ' ') });
}

export function statusTone(status) {
  switch (status) {
    case 'ACTIVE':
    case 'PENDING':
    case 'RUNNING':
    case 'OPEN':
    case 'WATCH':
      return 'live';
    case 'WHITE_WON':
    case 'BLACK_WON':
      return 'done';
    case 'HIGH':
      return 'muted';
    case 'DRAW':
    case 'LOW':
      return 'draw';
    case 'ABORTED':
    case 'CANCELLED':
      return 'muted';
    case 'COMPLETED':
      return 'done';
    default:
      return 'muted';
  }
}

export function PlayingStatusChip({ status, label }) {
  const tone = statusTone(status);
  return (
    <span className={`playing-status-chip playing-status-chip--${tone}`}>
      {tone === 'live' && <span className="playing-status-dot" aria-hidden />}
      {label || status}
    </span>
  );
}

/** Compact empty state — one short line + optional action (no giant empty cards). */
export function PlayingEmptyState({ title, hint, action }) {
  return (
    <div className="playing-empty playing-empty--compact">
      <p className="playing-empty-title">{title}</p>
      {hint && <p className="playing-empty-hint">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function PlayingBanner({ tone = 'danger', children, live = null }) {
  // Default polite for all tones — assertive live regions make browsers scroll
  // the page whenever a banner mounts (board "jumps"). Pass live="assertive"
  // only for hard blocks (e.g. platform ban) that must interrupt.
  const assertive = live === 'assertive';
  return (
    <div
      className={`playing-banner playing-banner--${tone}`}
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
    >
      {children}
    </div>
  );
}

export function PlayingSkeleton({ rows = 4, className = '' }) {
  return (
    <div className={`playing-skeleton ${className}`.trim()} aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="playing-skeleton-row">
          <div className="playing-skeleton-bar playing-skeleton-bar--lg" />
          <div className="playing-skeleton-bar playing-skeleton-bar--sm" />
        </div>
      ))}
    </div>
  );
}

export function PlayingBoardSkeleton() {
  return (
    <div className="playing-board-card" aria-busy="true" aria-label={i18n.t('common.loading', { defaultValue: 'Loading…' })}>
      <div className="playing-skeleton-bar playing-skeleton-bar--clock mb-3" />
      <div className="playing-skeleton-board" />
      <div className="playing-skeleton-bar playing-skeleton-bar--clock mt-3" />
    </div>
  );
}

export function colorPreferenceLabel(pref, t) {
  switch ((pref || '').toUpperCase()) {
    case 'WHITE':
      return t('playing.colorWhite');
    case 'BLACK':
      return t('playing.colorBlack');
    case 'RANDOM':
      return t('playing.colorRandom');
    default:
      return pref || t('playing.colorRandom');
  }
}

export function gameTypeLabel(gameType, t) {
  if (!gameType) return '';
  return t(`playing.gameType.${gameType}`, { defaultValue: gameType });
}

export function formatRatingChange(before, after) {
  if (before == null && after == null) return null;
  if (after == null) return { text: String(before), delta: null };
  if (before == null) return { text: String(after), delta: null };
  const delta = after - before;
  const sign = delta > 0 ? '+' : '';
  return {
    text: `${before} → ${after} (${sign}${delta})`,
    short: `${before} → ${after}`,
    delta,
  };
}

/** Material taken by each side + advantage for white (positive = white ahead). */
export function materialSnapshot(chess) {
  const start = { p: 8, n: 2, b: 2, r: 2, q: 1 };
  const vals = { p: 1, n: 3, b: 3, r: 5, q: 9 };
  const blackGlyphs = { p: '♟', n: '♞', b: '♝', r: '♜', q: '♛' };
  const whiteGlyphs = { p: '♙', n: '♘', b: '♗', r: '♖', q: '♕' };
  const onBoard = { w: { p: 0, n: 0, b: 0, r: 0, q: 0 }, b: { p: 0, n: 0, b: 0, r: 0, q: 0 } };
  for (const row of chess.board()) {
    for (const p of row) {
      if (p && p.type !== 'k') onBoard[p.color][p.type] += 1;
    }
  }
  const order = ['q', 'r', 'b', 'n', 'p'];
  let whiteTaken = '';
  let blackTaken = '';
  let whitePts = 0;
  let blackPts = 0;
  for (const type of order) {
    const takenByWhite = start[type] - onBoard.b[type];
    const takenByBlack = start[type] - onBoard.w[type];
    // White’s captures are black pieces; black’s captures are white pieces
    whiteTaken += blackGlyphs[type].repeat(Math.max(0, takenByWhite));
    blackTaken += whiteGlyphs[type].repeat(Math.max(0, takenByBlack));
    whitePts += Math.max(0, takenByWhite) * vals[type];
    blackPts += Math.max(0, takenByBlack) * vals[type];
  }
  return {
    whiteCaptured: whiteTaken,
    blackCaptured: blackTaken,
    whiteAdvantage: whitePts - blackPts,
  };
}

export function MoveList({
  history = [],
  emptyLabel,
  activePly = null,
  interactive = false,
  onSelectPly,
  qualitiesByPly = null,
}) {
  const endRef = useRef(null);
  const pairs = [];
  for (let i = 0; i < history.length; i += 2) {
    pairs.push({ n: Math.floor(i / 2) + 1, w: history[i], b: history[i + 1], wPly: i + 1, bPly: i + 2 });
  }

  useEffect(() => {
    if (activePly == null || activePly >= history.length) {
      endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [history.length, activePly]);

  if (!pairs.length) {
    return <p className="playing-moves-empty">{emptyLabel}</p>;
  }

  const plyBtn = (san, ply) => {
    if (!san) return <span className="playing-moves-san" />;
    const active = activePly === ply;
    const quality = qualitiesByPly?.[ply];
    const qualityClass = quality ? `playing-moves-san--${quality}` : '';
    const className = `playing-moves-san ${active ? 'playing-moves-san--active' : ''} ${interactive ? 'playing-moves-san--clickable' : ''} ${qualityClass}`.trim();
    if (!interactive) {
      return <span className={className}>{san}</span>;
    }
    return (
      <button
        type="button"
        className={className}
        aria-current={active ? 'true' : undefined}
        onClick={() => onSelectPly?.(ply)}
      >
        {san}
      </button>
    );
  };

  return (
    <div className="playing-moves" role="list">
      {pairs.map((p) => (
        <div key={p.n} className="playing-moves-row" role="listitem">
          <span className="playing-moves-n">{p.n}.</span>
          {plyBtn(p.w, p.wPly)}
          {plyBtn(p.b, p.bPly)}
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}

export function PlayerClockBar({
  name,
  userId,
  rating,
  ratingAfter,
  captured,
  materialAdv,
  clockMs,
  active,
  low,
  critical,
  colorLabel,
  className = '',
}) {
  const ratingChange = formatRatingChange(rating, ratingAfter);
  return (
    <div
      className={[
        'playing-player-bar',
        active ? 'playing-player-bar--active' : '',
        low ? 'playing-player-bar--low' : '',
        critical ? 'playing-player-bar--critical' : '',
        className,
      ].filter(Boolean).join(' ')}
      aria-current={active ? 'true' : undefined}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={`playing-color-pip playing-color-pip--${colorLabel}`} aria-hidden />
          <p className="truncate text-sm font-semibold text-slate-900">
            <UserLink
              id={userId}
              name={name || '…'}
              playing
              className="text-slate-900 hover:text-primary-700"
            />
          </p>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-5 text-xs tabular-nums text-slate-500">
          {ratingChange && (
            <span>
              {ratingChange.short || ratingChange.text}
              {ratingChange.delta != null && ratingChange.delta !== 0 && (
                <span className={ratingChange.delta > 0 ? 'text-emerald-600' : 'text-red-600'}>
                  {' '}
                  ({ratingChange.delta > 0 ? '+' : ''}
                  {ratingChange.delta})
                </span>
              )}
            </span>
          )}
          {(captured || materialAdv) && (
            <span className="playing-material" title={i18n.t('playing.material', { defaultValue: 'Material' })}>
              {captured}
              {materialAdv != null && materialAdv !== 0 && (
                <span className="ml-1 font-semibold text-slate-700">
                  {materialAdv > 0 ? `+${materialAdv}` : materialAdv}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
      <div
        className={`playing-clock ${low ? 'playing-clock--low' : ''} ${critical ? 'playing-clock--critical' : ''} ${active ? 'playing-clock--active' : ''}`}
        aria-live="off"
      >
        {formatClock(clockMs)}
      </div>
    </div>
  );
}

/** Shared row for live / my-games lists */
export function GameListRow({
  game,
  href,
  actionLabel,
  analyzeHref,
  playAgainHref,
  onRematch,
  rematchBusy = false,
}) {
  const { t } = useTranslation();
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!game.moveDeadlineAt) return undefined;
    const active = !game.endedAt && (game.status === 'ACTIVE' || game.status === 'PENDING');
    if (!active) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [game.moveDeadlineAt, game.endedAt, game.status]);

  const tc = formatTimeControl(
    game.timeControlSeconds,
    game.incrementMs != null ? Math.round(game.incrementMs / 1000) : 0,
    game.daysPerMove,
  );
  const statusLabel = t(`playing.status.${game.status}`, { defaultValue: game.status });
  const typeLabel = gameTypeLabel(game.gameType, t);
  const reason = formatResultReason(game.resultReason, t);
  const when = game.endedAt || game.startedAt || game.lastMoveAt;
  const whenLabel = when ? formatDateTime(when) : '';
  const deadlineLeft = formatDeadlineCountdown(game.moveDeadlineAt, now);
  const urgency = deadlineUrgency(game.moveDeadlineAt, now);
  const isActive = !game.endedAt && (game.status === 'ACTIVE' || game.status === 'PENDING');

  return (
    <li className="playing-game-row">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-slate-900">
            <span className="playing-side-dot playing-side-dot--w" aria-hidden />
            <UserLink id={game.whiteUserId} name={game.whiteName} playing className="text-slate-900 hover:text-primary-700" />
            <span className="mx-1.5 text-slate-300">vs</span>
            <span className="playing-side-dot playing-side-dot--b" aria-hidden />
            <UserLink id={game.blackUserId} name={game.blackName} playing className="text-slate-900 hover:text-primary-700" />
          </p>
          <PlayingStatusChip status={game.status} label={statusLabel} />
          {isActive && game.myTurn && (
            <span className="playing-meta-pill playing-meta-pill--accent">
              {t('playing.yourMove')}
            </span>
          )}
          {game.clubReviewReady && game.myAccuracyPct != null && (
            <span className="playing-meta-pill playing-meta-pill--accent">
              {t('playing.accuracyChip', {
                defaultValue: '{{pct}}% accuracy',
                pct: game.myAccuracyPct,
              })}
            </span>
          )}
          {game.clubReviewReady && game.myAccuracyPct == null && (
            <span className="playing-meta-pill playing-meta-pill--accent">
              {t('playing.reviewReady', { defaultValue: 'Review ready' })}
            </span>
          )}
          {!game.clubReviewReady
            && (game.moveCount === 0 || game.moveCount === '0')
            && !isActive
            && (game.resultReason === 'RESIGN' || game.resultReason === 'TIMEOUT' || game.resultReason === 'ABORT')
            && (
            <span className="playing-meta-pill">
              {t(`playing.resultReason.${game.resultReason}`, {
                defaultValue: game.resultReason,
              })}
            </span>
          )}
          {isActive && deadlineLeft && (
            <span
              className={`playing-meta-pill ${
                urgency === 'critical'
                  ? 'playing-meta-pill--danger'
                  : urgency === 'warn'
                    ? 'playing-meta-pill--warn'
                    : ''
              }`}
            >
              {t('playing.moveDeadlineShort', {
                when: deadlineLeft,
                defaultValue: 'Move by {{when}}',
              })}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          {typeLabel}
          {tc ? ` · ${tc}` : ''}
          {game.rated ? ` · ${t('playing.ratedGame')}` : ` · ${t('playing.casualGame')}`}
          {reason ? ` · ${reason}` : ''}
          {whenLabel ? ` · ${whenLabel}` : ''}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {analyzeHref && (
          <Link to={analyzeHref} className="btn-page-action-secondary px-3 py-1.5 text-sm">
            {t('playing.analyze', { defaultValue: 'Analyze' })}
          </Link>
        )}
        {playAgainHref && (
          <Link to={playAgainHref} className="btn-page-action-secondary px-3 py-1.5 text-sm">
            {t('playing.playAgain', { defaultValue: 'Play again' })}
          </Link>
        )}
        {typeof onRematch === 'function' && (
          <button
            type="button"
            className="btn-page-action-secondary px-3 py-1.5 text-sm disabled:opacity-50"
            disabled={rematchBusy}
            onClick={onRematch}
          >
            {rematchBusy
              ? t('playing.rematchSending', { defaultValue: 'Sending…' })
              : t('playing.rematch', { defaultValue: 'Rematch' })}
          </button>
        )}
        {href ? (
          <Link to={href} className="btn-page-action-secondary px-3 py-1.5 text-sm">
            {actionLabel}
          </Link>
        ) : (
          <span className="playing-meta-pill text-slate-500">
            {t('playing.privateGame', { defaultValue: 'Private' })}
          </span>
        )}
      </div>
    </li>
  );
}
